/**
 *
 * V1.7
 *
 * User: itaik
 * Date: 12/24/15
 *
 * Changes:
 *
 * Added support for label (with behaviour like the inline textarea label) [option: label]
 * Added support for allowing null values while keeping editable mode [option: allowNull]
 * Added support for left caption [option: caption) - caption is used instead of label when the field is opened (hence requires the existence of a label)
 *
 *
 * *******************************************************************************************************
 *
 * V1.6
 *
 * User: shlomif
 * Date: 5/1/2014
 *
 * + Add unit test
 *
 * + Updated documentation: (to be improved!)
 *
 * Options:
 *
 * + container - mandatory. jQuery element. The element on the page where the component will be created.
 * + value
 * + placeholder
 * + maxLength
 * + enabled
 * + permutation
 * + errorPosition
 * + autoFocus
 * + tooltip
 *
 * Public methods:
 *
 * + setEnabled
 * + setFocus
 * + setValue
 * + getValue
 * + setError
 *
 * * *******************************************************************************************************
 *
 * V1.5
 *
 * User: danielr
 * Date: 11/21/13
 *
 * Changes:
 *
 * Added tooltip feature for the input
 * Options:
 * tooltip - optional.
 *
 *
 * *******************************************************************************************************
 *
 * V1.4
 *
 * User: tomers
 * Date: 4/21/13
 * Visual PDF: https://drive.google.com/a/liveperson.com/?tab=mo#folders/0BzCszeQVU73yUU8wdVdsMW41Qzg
 *
 * Changes:
 *
 * Change inputWidth, inputHeight and inputFontSize to one attribute called 'permutation'.
 * As for now we support 6 permutations.
 * Options:
 * permutation - optional. One of the options in the static InlineInput.PERMUTATION enum. PERMUTATION.A is the default
 *
 *
 * *******************************************************************************************************
 *
 * V1.1
 *
 * User: shaulm
 * Date: 3/21/13
 * Visual PDF: https://docs.google.com/a/liveperson.com/folder/d/0BzCszeQVU73yUU8wdVdsMW41Qzg/edit?docId=0B7O_LAPhjkbkTGRxaXU2UE5mMkU
 *
 * This widget is used to implement an inline input  component on the page.
 * Currently we support 2 sizes (medium and xxlarge).
 * Options:
 * el - mandatory. jQuery element. The element on the page where the component will be created.
 * model - mandatory. Marionette View. The model of the view that using this component.
 * bindMap - mandatory. Object. Mapping between the component model attributes and the external view model attribute. For example: {'value':'description'}
 * placeholder - optional. String. The placeholder appears when there is no value on editable mode. Should be the 'data-placeholder' attribute on the 'el' HTML element (also, can be sent as part of the options)
 * inputSize - optional. One of the options in the static InlineInput.HEIGHT and WIDTH enum (MEDIUM / XXLARGE). MEDIUM is the default
 *
 * Using Example:
 *---------------------------
 * template:
 *<div class="campaignName" data-placeholder="{{#_i18n}}LECampaigns.CampaignName{{/_i18n}}" >{{name}}</div>
 *
 *  js:
 *  var campaignName = new InlineInput({
 *      model:this.model,
 *      bindMap : {
 *          'value' : 'name'
 *      },
 *      el:this.ui.campaignName
 *  });
 *  campaignName.render();
 *
 * *******************************************************************************************************
 */


define(function (require) {
    "use strict";

    var $ = require('jquery');
    var _ = require('underscore');
    var Marionette = require('marionette');
    var Modernizr = require('modernizr');
    var template = require('ui/inline-input/templates/inlineInput.tmpl');
    var InlineInputModel = require('ui/inline-input/js/models/InlineInputModel');
    var Bubble = require("ui/bubble/Bubble");
    require('ui.plugins/jquery.inputCursor');

    var ENTER_KEYCODE = 13;
    var EMPTY_VALUE = "";

    var InlineInputView = Marionette.ItemView.extend({
        template:template,
        withUIComponents: true,
        errorMsg: null,
        events:{
            "click div.input":"onInputValueClick",
            "focus input.input":"onInputFocus",
            "blur input.input":"onInputBlur",
            "keydown input.input":"onInputKeyDown",
            "click div.add-label span": "onLabelClick"
        },

        ui:{
            caption: "div.caption",
            input: "input.input",
            text: "div.input",
            label:"div.add-label"
        },

        placeholderIsSet: false, // Local member that indicates we are showing a manual placeholder or not. Relevant only in non-HTML5 browsers.
        className: "ui-comp-inline-input",
        initialize:function (options) {
            this.container = options.container;

            var inlineInputModel = new InlineInputModel();

            var placeholder = $(options.container).attr("data-placeholder") || options.placeholder;
            inlineInputModel.set("placeholder", placeholder);

            var caption = $(options.container).attr("data-caption") || options.caption;
            inlineInputModel.set(InlineInputView.CAPTION, caption);

            var label = $(options.container).attr("data-label") || options.label;
            inlineInputModel.set(InlineInputView.LABEL, label);

            var initValue = options.value;
            this.allowNull = options.allowNull || false;

            if (!this.allowNull && !initValue) {
                initValue = EMPTY_VALUE;
            }

            if ((this.allowNull || caption) && !label) {
                throw new Error("allowNull and caption fields require existence of a label");
            }

            inlineInputModel.set(InlineInputView.VALUE, initValue);

            var maxLength = options.maxLength || inlineInputModel.get("maxLength");
            inlineInputModel.set("maxLength", maxLength);

            var enabled = _.has(options, "enabled") ? options.enabled : inlineInputModel.get("enabled");
            inlineInputModel.set("enabled", enabled);

            this.version = "1.7";

            this.model = inlineInputModel;
            this.permutation = options.permutation || InlineInputView.PERMUTATION.A;
            this.nightTheme = _.has(options, "nightTheme") ? options.nightTheme : false;
            this.errorPosition = options.errorPosition || InlineInputView.ERROR_POSITION.BOTTOM;
            this.listenTo(this.model, 'change:' + InlineInputView.VALUE, this.show);

            this.autoFocus = options.autoFocus || false;
            this.tooltip = _.has(options, "tooltip") ? options.tooltip : "";
        },

        onRender:function () {
            var containerClassByPermutation = this._getContainerClassByPermutation();
            this.container.addClass(containerClassByPermutation);
            this.container.append(this.$el);

            if (this.permutation === InlineInputView.PERMUTATION.J) {
                this.ui.text.addClass("ellipsis");
            }

            this.setFocus(this.autoFocus);
        },

        /**
         * show dom elements
         */
        show:function () {
            var val = this.model.get(InlineInputView.VALUE);
            var label = this.model.get(InlineInputView.LABEL);
            var caption = this.model.get(InlineInputView.CAPTION);

            if (!_.isUndefined(val) && !_.isNull(val) && val !== EMPTY_VALUE) {
                this.ui.text.text(val);
                this.ui.text.css("display", "inline-block");
                this.ui.text.attr("title", this.tooltip);
                this.ui.caption.css("display", caption ? "inline-block" : "none");
                this.ui.input.css("display", "none");
                this.ui.label.css("display", "none");
            }
            else if (_.isNull(val) && this.allowNull && label) {
                this.ui.input.val(EMPTY_VALUE);
                this.ui.label.css("display", "inline-block");
                this.ui.input.css("display", "none");
                this.ui.text.css("display", "none");
                this.ui.caption.css("display", "none");
                this._setPlaceholder();
            }
            else {
                this.ui.input.val(EMPTY_VALUE);
                this.ui.input.css("display", "inline-block");
                this.ui.caption.css("display", caption ? "inline-block" : "none");
                this.ui.text.css("display", "none");
                this.ui.label.css("display", "none");
                this._setPlaceholder();
            }
        },

        /**
         * Triggered when the input is on focus
         */
        onInputFocus:function () {
            if (this.ui.input.hasClass("error") && this.errorMsg) {
                this.uiComponents.host$.createAndRender("InlineInputError", {
                    type:Bubble,
                    options:{
                        targetControl:this.ui.input,
                        text:this.errorMsg,
                        position:this.errorPosition,
                        icon:Bubble.ICON.ERROR,
                        showCloseButton:false
                    }
                });
            }
            this._removePlaceholder();
        },

        /**
         * Triggered when the input is blur
         */
        onInputBlur:function () {
            this.setError(false);

            var trimmedValue = this.ui.input.val().trim();
            var val = this.model.get(InlineInputView.VALUE);

            if ((trimmedValue === EMPTY_VALUE && !_.isUndefined(val) && !_.isNull(val) && val !== EMPTY_VALUE && !this.allowNull) || (val === trimmedValue)) {
                this.show();
            } else {
                this._setValue(trimmedValue);
            }

            this.trigger("blur", this.getValue());
        },

        /**
         * Triggered on any key down.
         * Check for "Enter" and call the "inputBlur" method as the "Enter" should act like the user blurs
         * @private
         */
        onInputKeyDown: function(e) {
            if (e && e.keyCode === ENTER_KEYCODE) {
                this.ui.input.blur();
            }
        },

        /**
         * Put the cursor in the input field. When the field has a text, 'select' the text.
         */
        _focusInput: function(){
            if (this.ui.input.inputcursor) { //Because we use setTimeOut, we can get here after the component is closed.
                if (this.model.id) {
                    this.ui.input.inputcursor('focusEnd'); // Is this code reachable?
                } else {
                    this.ui.input.inputcursor('selectAll');
                }
            }
        },

        /**
         * Triggered when the user clicks on the input value
         */
        onInputValueClick:function () {
            if (this.model.get("enabled")) {
                this._showInput();
                this._focusInput();
            }
        },

        /**
         * Triggered when user clicks on 'add description label' and moves to editable mode
         */
        onLabelClick:function () {
            if (this.model.get("enabled")) {
                this._showInput();
                this._focusInput();
            }
        },

        setTooltip: function(tooltip) {
            if (!_.isUndefined(tooltip) && !_.isNull(tooltip)) {
                this.tooltip = tooltip;
                this._updateTooltip();
            }
        },

        _updateTooltip: function() {
            this.ui.text.attr("title", this.tooltip);
            this.ui.text.attr("tooltip-text", this.tooltip);
        },

        _showInput: function() {
            var val = this.model.get(InlineInputView.VALUE);
            var caption = this.model.get(InlineInputView.CAPTION);
            this.ui.input.val(val);
            this.ui.text.css("display", "none");
            this.ui.label.css("display", "none");
            this.ui.input.css("display", "inline-block");
            this.ui.caption.css("display", caption ? "inline-block" : "none");
        },

        /**
         * For non HTML5 browsers we place a placeholder when the user is not focused on the input and there is no value in it.
         */
        _setPlaceholder:function () {
            if (!Modernizr.input.placeholder) {
                if (!this.placeholderIsSet) {
                    this.placeholderIsSet = true;
                    this.ui.input.addClass('placeholder');
                    this.ui.input.val(this.ui.input.attr('placeholder'));
                }
            }
        },

        setEnabled: function(mode) {
            this.model.set({"enabled": mode});
        },

        /**
         * For non HTML5 browsers we remove the placeholder we put manually in the input
         */
        _removePlaceholder:function () {
            if (!Modernizr.input.placeholder) {
                if (this.placeholderIsSet) {
                    this.ui.input.val(EMPTY_VALUE);
                    this.ui.input.removeClass('placeholder');
                    this.placeholderIsSet = false;
                }
            }
        },

        /**
         * Returns the relevant class to attach to the container by the permutation
         * @return {String} - Class name
         * @private
         */
        _getContainerClassByPermutation:function () {
            var retClass = "input-container-perm-" + this.permutation;

            if (this.nightTheme) {
                retClass += " input-container-night-theme";
            }

            return retClass;
        },

        setFocus:function (focus) {
            if(focus){
                this._showInput();

                setTimeout(_.bind(function() {
                    this._focusInput();
                 }, this), 1);
            }
            else{
                this.show();
            }
        },

        /**
         * Set the value in the internal model and triggers an event to whoever is listening to change on this view-component
         * @param value. String. The value to update the model with.
         * @param options Object. silent:true will not trigger the change event
         * @private
         */
        _setValue:function (value, options) {
            options = options || {};

            this.model.set(InlineInputView.VALUE, value);

            if (!options.silent) {
                this.trigger("change", value);
            }
        },

        /**
         * Public method to change the value in the component.
         * As default, if the value is set from other view and not through the DOM, we are not triggers the event because we assume that whoever call this method already knows that the value has been changed.
         * @param value. String. The value to update the model with.
         */
        setValue:function (value, options) {
            options = options || {};
            options.silent = _.has(options, "silent") ? options.silent : true;

            this._setValue(value, options);
        },

        getValue:function () {
            return this.model.get(InlineInputView.VALUE);
        },

        setError:function (isError, msg) {
            if (isError === true) {
                this._showInput();
                this.errorMsg = msg;
                this.ui.input.addClass("error");
            }
            else {
                this.ui.input.removeClass("error");
                this.errorMsg = null;
                //if (this.uiComponents.hasComponent("InlineInputError")) {
                if (this.uiComponents.InlineInputError) {
                    this.uiComponents.InlineInputError.close();
                }
            }
        },

        onClose:function () {
            var containerClassByPermutation = this._getContainerClassByPermutation();
            this.container.removeClass(containerClassByPermutation);
        }
    });

    /**
     * Public static eNum of the optional permutation
     */
    InlineInputView.PERMUTATION = {
        A:1,
        B:2,
        C:3,
        D:4,
        E:5,
        F:6,
        G:7,
        H:8,
        I:9,
        J:10
    };
    InlineInputView.ERROR_POSITION = Bubble.POSITION;
    InlineInputView.VALUE = "value";
    InlineInputView.LABEL = "label";
    InlineInputView.CAPTION = "caption";

    return InlineInputView;
});
